import classNames from 'classnames';
import type { ReactNode } from 'react';
import { createRef, forwardRef, useImperativeHandle } from 'react';
import Slider from 'react-slick';
import type { ActionProps } from '../../contentful-components/action-button';
import { ActionButton } from '../../contentful-components/action-button';
import { usePersonalizations } from '../../lib/hooks/usePersonalizations';
import { formatRelativeDate } from '../../lib/utils/date';
import { renderTemplate, TEXT } from '../../lib/utils/text';
import { iconSizeSmall, iconSizeXSmall, profileCardGifHeight } from '../../styles/variables.module.scss';
import type { VideoType } from '../../types/video';
import { Avatar } from '../avatar';
import { Box } from '../box';
import type { ButtonProps } from '../button/button';
import { Button } from '../button/button';
import { Heading } from '../heading';
import { Dollar } from '../icons/dollar';
import { Logo } from '../icons/logo';
import { Paragraph } from '../paragraph';
import { ShareQRCode } from '../share-qr-code';
import { Skeleton } from '../skeleton';
import { Video } from '../video';
import styles from './profile-card.module.scss';

/**
 * Displays a card with a name, avatar, date they joined,
 * and a random collection of gifs, for funzies.
 */
export const ProfileCard = forwardRef<ProfileCardHandle, Props>(({
  displayName,
  username,
  createdAtMs,
  pictureUrl,
  subtitle,
  primary = {
    title: TEXT.getStep,
    type: 'ActionDownloadApp'
  },
  secondary,
  loading,
  hasAward,
  body,
  videos,
  layout = 'fixed',
  headingPrefix = ''
}, ref) => {
  const createdAt = createdAtMs ? new Date(+createdAtMs) : undefined;
  const sliderRef = createRef<Slider>();
  const personalizations = usePersonalizations();
  const referredAwardFormatted = hasAward ? personalizations.referredAwardFormattedShort : undefined;
  useImperativeHandle(ref, () => ({
    nextVideo: () => sliderRef.current?.slickNext()
  }));
  return <div className={classNames(styles.card, styles[layout])} data-testid='profile.card'>
                {videos.length > 0 ? <div className={styles.head}>
                        <Slider arrows={false} slidesToShow={1} slidesToScroll={1} ref={sliderRef} className={styles.video} vertical fade lazyLoad='ondemand'>
                            {videos.map(({
          sources,
          ...video
        }, i) => <Video {...video} key={sources.find(s => !!s)?.source ?? i} sources={sources} width='100%' height={profileCardGifHeight} />)}
                        </Slider>
                    </div> : <Skeleton className={styles.video} width='100%' />}

                {username && <ShareQRCode data-testid='profile.qr.button' username={username} name={displayName} pictureUrl={pictureUrl} className={styles.qrcode} />}

                <div className={styles.avatar}>
                    <Avatar data-testid='profile.avatar' name={displayName?.toLowerCase() === TEXT.someone ? '' : displayName} size='xlarge' source={pictureUrl} badge={referredAwardFormatted} />
                </div>

                <div className={styles.info}>
                    {loading ? <>
                            <Paragraph loading numberOfLines={2} />
                            <hr className={styles.hr} />
                            <Paragraph loading numberOfLines={1} />
                        </> : <>
                            {displayName && <div>
                                    <Heading level={1} className={styles.name} data-testid='profile.name'>
                                        {headingPrefix} {displayName}
                                    </Heading>
                                </div>}

                            {username && <div data-testid='profile.username' className={styles.username}>
                                    ${username}
                                </div>}

                            {subtitle && <div data-testid='profile.subtitle' className={styles.subtitle}>
                                    {subtitle}
                                </div>}

                            {!!(createdAt || referredAwardFormatted || body) && <hr className={styles.hr} />}

                            {!!body && <div className={styles.body}>{body}</div>}

                            {createdAt && <time data-testid='profile.date' className={styles.ps} dateTime={createdAt.toISOString()}>
                                    <Logo className={classNames(styles.logo, styles.icon)} preset='line' height={iconSizeXSmall} />
                                    {renderTemplate(TEXT.joinedAt, {
            date: formatRelativeDate(createdAt)
          })}
                                </time>}

                            {referredAwardFormatted && <div data-testid='profile.award' className={classNames(styles.ps, styles.award)}>
                                    <Dollar className={styles.icon} height={iconSizeSmall} />
                                    {renderTemplate(TEXT.getUSDWhenYouSignUp, personalizations)}
                                </div>}
                        </>}

                    <div className={classNames(styles.cta)}>
                        <Box pad={['horizontal']}>
                            {primary && <ActionButton {...primary} />}
                            {secondary && <Button type='button' preset='secondary' size='medium' className={styles.secondary} {...secondary} />}
                        </Box>
                    </div>
                </div>
            </div>;
});
export type ProfileCardHandle = {
  nextVideo: () => void | undefined;
};
type Props = ProfileCardProps;
export interface ProfileCardProps {
  /**
   * Date the user joined Step, in ms.
   */
  createdAtMs?: string;

  /**
   * Avatar source.
   */
  pictureUrl?: string;

  /**
   * The display name of the profile.
   */
  displayName?: string;

  /**
   * Username for display.
   */
  username?: string;

  /**
   * Subtitle for the profile.
   */
  subtitle?: string;

  /**
   * Indicates the card is in loading state.
   */
  loading?: boolean;

  /**
   * Props for the primary button, which is always some kind of "Download app" button.
   */
  primary?: ActionProps | null;

  /**
   * Props for the secondary button, which can do anything.
   */
  secondary?: Omit<ButtonProps, 'size' | 'preset'>;

  /**
   * Optional body to render, below subtitle and above the CTA.
   */
  body?: ReactNode;

  /**
   * Layout of the card. Defaults to fixed height and width.
   * @see `$profile-card-height` from `dimensions.scss`
   */
  layout?: 'fixed' | 'fluid';

  /**
   * If true, referral award will be shown.
   */
  hasAward?: boolean;

  /**
   * Videos to shuffle on the card.
   */
  videos: VideoType[];

  /**
   * Prefix for the heading.
   */
  headingPrefix?: string;
}